import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/shared/ui/dialog";
import { Button } from "@/shared/ui/button";
import { Textarea } from "@/shared/ui/textarea";
import { Loader2 } from "lucide-react";
import { useState, useEffect } from "react";
import { useUpdateServiceLineNotes } from "@/domains/service-lines/services/account-service-lines";
import { ERROR, SUCCESS } from "@/config/toast-message-config";
import { showErrorWithApiError, showSuccess } from "@/shared/utils/toast-utils";
import { ServiceLine } from "@/shared/types/serviceLineTypes";

interface ServiceLineNotesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  serviceLine: ServiceLine;
}

const ServiceLineNotesDialog = ({
  open,
  onOpenChange,
  onSuccess,
  serviceLine,
}: ServiceLineNotesDialogProps) => {
  const serviceLineNotes = useUpdateServiceLineNotes();
  const [notes, setNotes] = useState<string>(serviceLine.notes ?? '');

  useEffect(() => {
    setNotes(serviceLine.notes ?? '');
  }, [serviceLine.notes]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      await serviceLineNotes.mutateAsync({
        serviceLineNumber: serviceLine.serviceLineNumber,
        notes: notes.trim(),
      });
      showSuccess(SUCCESS.SERVICE_LINE.NOTES);
      onSuccess();
      onOpenChange(false);
      // Reset form
      setNotes('');
    } catch (error) {
      showErrorWithApiError(error, ERROR.SERVICE_LINE.NOTES);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Service Line Notes</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 gap-4">
            <div className="space-y-2">
              <Textarea
                id="notes"
                name="notes"
                rows={10}
                placeholder="Enter Service Line Notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              className="gap-2"
              onClick={() => onOpenChange(false)}
              disabled={serviceLineNotes.isPending}

            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={serviceLineNotes.isPending}
              className="gap-2">
              {serviceLineNotes.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Submit'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default ServiceLineNotesDialog; 